/**
 * Shapes for the third vendor, Shen.AI.
 *
 * **Written from the vendor's own `index.d.ts`, not recovered from a bundle.** That is the one
 * material difference from Mindset, and it is worth stating because two of this repository's worst
 * bugs came from the absence of exactly this: the run token typed as a string when the SDK hands its
 * callback an object (D-21), and the licence mangled by a contract this app invented for it (D-22).
 * Shen ships types, they are vendored beside the runtime at
 * public/sdk/shenai/index.d.ts, and the declarations below are read off them.
 *
 * They are still declared HERE rather than imported from the package, for the same reason the
 * runtime is vendored rather than bundled: nothing in this app's build graph may reach into a 33 MB
 * vendor package, and `browser-shenai-client.ts` loads that directory over HTTP at runtime. The
 * devDependency exists so a typo here fails `tsc` against the vendor's own declarations rather than
 * in front of a patient.
 */

/** The vendor's enum members arrive as boxed values, not numbers: `{ value: 3 }`. */
export interface ShenAiEnumValue {
  readonly value: number
}

/**
 * How the measurement is going, in the vendor's own vocabulary
 * (`MeasurementState` in the vendor's enums).
 *
 * Named rather than numbered because this app reads them: the vendor's UI is switched off and these
 * are what drive this app's own screen (docs/decisions.md D-32).
 */
export const SHENAI_MEASUREMENT_STATE = {
  NOT_STARTED: 0,
  WAITING_FOR_FACE: 1,
  RUNNING_SIGNAL_SHORT: 2,
  RUNNING_SIGNAL_GOOD: 3,
  RUNNING_SIGNAL_BAD: 4,
  RUNNING_SIGNAL_BAD_DEVICE_UNSTABLE: 5,
  FINALIZING: 6,
  FINISHED: 7,
  FAILED: 8,
} as const

/** Where the patient's face is, as the vendor sees it (`FaceState`). */
export const SHENAI_FACE_STATE = {
  OK: 0,
  TOO_FAR: 1,
  TOO_CLOSE: 2,
  NOT_CENTERED: 3,
  NOT_VISIBLE: 4,
  TURNED_AWAY: 5,
  /** The vendor has not determined a state yet. Not a problem, and not shown - see shenai-events.ts. */
  UNKNOWN: 6,
} as const

/** What `initialize` answers with (`InitializationResult`). */
export const SHENAI_INITIALIZATION_RESULT = {
  OK: 0,
  INVALID_API_KEY: 1,
  CONNECTION_ERROR: 2,
  INTERNAL_ERROR: 3,
} as const

/**
 * One measurement, as `getMeasurementResults()` returns it.
 *
 * Only the fields this app reads are declared. The vendor returns a great deal more - HRV, stress
 * index, parasympathetic activity, cardiac workload, an estimated age and BMI - and none of it is
 * on `SubmitScanRequest`, so none of it is declared here or logged anywhere it could be mistaken
 * for something this platform stores.
 *
 * **There is no blood oxygen.** Shen measures none, which is why `spo2` is absent below and omitted
 * from the submit body rather than sent as a zero or a minus one (shenai-result.ts).
 */
export interface ShenAiMeasurementResults {
  readonly heart_rate_bpm: number
  readonly breathing_rate_bpm: number | null
  readonly systolic_blood_pressure_mmhg: number | null
  readonly diastolic_blood_pressure_mmhg: number | null
  readonly average_signal_quality: number
}

/**
 * The seam, mirroring the part of the vendor's `ShenaiSDK` this app uses.
 *
 * It exists for the reason `MindsetVitalClient` and `FhVitalsClient` do: a scan needs a camera, a
 * 33 MB WASM module and a real minute, and none of those belong in a unit test. Everything with a
 * decision in it is in shenai-session.ts behind this interface.
 *
 * **Polling, not events.** Shen exposes getters rather than an emitter, so the runner reads this
 * interface on a timer where the Mindset runner subscribes. That is the vendor's shape and it is
 * reproduced rather than hidden: a fake that answered events would test a client this app does not
 * have.
 */
export interface ShenAiVitalClient {
  /** Hands the SDK the camera this app already opened inside the patient's tap (D-24). */
  setMediaStream(stream: MediaStream): void
  /**
   * Downloads and instantiates the vendor's 33 MB runtime, reporting its own progress as a fraction
   * of one.
   *
   * SEPARATE FROM `initialize`, and that separation is the whole point: this is the slow step by two
   * orders of magnitude, and while it was folded into initialize the patient sat on "Almost there"
   * for the entire download - the phase that is meant to mean "the licence call is in flight"
   * (docs/decisions.md D-34).
   */
  load(onProgress?: (fractionComplete: number) => void): Promise<void>
  /** Resolves with the vendor's own initialization verdict; never throws for a refused token. */
  initialize(token: string, userId: string): Promise<number>
  startMeasurement(): void
  stopMeasurement(): void
  measurementState(): number
  faceState(): number
  /** 0..100, the vendor's own account of how far through the measurement it is. */
  progressPercentage(): number
  results(): ShenAiMeasurementResults | null
  destroy(): void
  /**
   * Gives the vendor's 33 MB Emscripten runtime back to the browser, which `destroy()` does NOT do.
   *
   * **Separate from `destroy()` because it is the dangerous half.** `destroy()` deinitializes the
   * pipeline and stops the camera, and deliberately does not call the vendor's `destroyRuntime()`:
   * on iOS that produced `Aborted()` and "Unreachable code should not be executed" - a WASM trap in
   * the console of a patient whose scan had already finished - so D-35 left it out on the grounds
   * that nothing needed it, the page being about to navigate or close either way.
   *
   * A SEQUENTIAL scan needs it, which is what changed: the second vendor's 55 MB cannot be loaded on
   * top of the first vendor's heap on a device that could not hold both at once
   * (docs/decisions.md D-38, D-42). So the call D-35 refused is made here, on its own method, by the
   * one runner that has a reason - after the reading has already been submitted, so a trap costs the
   * second half of the scan and never the measurement.
   *
   * Optional on the interface: a fake has nothing to release, and a client that cannot release throws
   * nothing.
   */
  releaseRuntime?(): void
}

/** What the factory takes. The token is minted by this service and is never the clinic's own key. */
export interface ShenAiVitalClientOptions {
  /** Opened by the caller, in the patient's tap. Null on the journey's recorded-footage path. */
  readonly stream?: MediaStream | null
}
